/**
 * Menu DOM-id helpers.
 *
 * Kept in its own import-free module so the pure logic stays unit-testable
 * without pulling in React or `@forcepoint/platform-coreui` (which resolves
 * at runtime through the SystemJS import map, not from node_modules).
 */

/** Returned when a value yields no selector-safe characters at all. */
const UNUSABLE = "unknown"

/**
 * Normalizes one segment of a DOM `id` into a selector-safe token.
 *
 * `applicationCode`, `localizationKey` and `name` all come from the
 * /api/applications payload and are unconstrained strings — real menus
 * include values like "Feature Flags", "External API" and "Policy Rules".
 * An id containing whitespace is invalid HTML and, more importantly, makes
 * `#id` lookups unusable — which would defeat the whole point of the id
 * (Gainsight PX targets these elements by selector). Collapsing every run
 * of non-alphanumerics to a single dash keeps the result deterministic and
 * stable across renders.
 */
export const toIdToken = (value: string | null | undefined): string =>
  // Split on runs of non-alphanumerics and drop the empty pieces, rather
  // than joining and then trimming stray dashes off the ends. Leading and
  // trailing separators simply become empty segments that filter() removes,
  // so the result needs no trim pass at all.
  //
  // This shape is deliberate: the obvious trim, `.replace(/^-+|-+$/g, "")`,
  // mixes anchors with alternation (sonar S5850) and its `-+$` half is a
  // backtracking risk on pathological input (sonar S5852). Splitting has
  // no anchors, no alternation and no end-anchored quantifier, so neither
  // applies — and it reads as what it actually does.
  String(value ?? "")
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .join("-") || UNUSABLE

/**
 * Builds the id token for one menu item, falling back to its `url` when the
 * label yields nothing usable.
 *
 * MenuItemData carries no backend identifier, so the label is all we have to
 * name an item by. A menu whose `localizationKey`/`name` is missing or
 * punctuation-only normalizes to "unknown", and two such siblings would
 * collide; `url` is distinct per menu and — unlike a positional index —
 * stays stable when the backend reorders or feature-flags menus, which
 * matters because Gainsight PX selectors are tagged against these ids and
 * an order-derived id would silently orphan a tag on every reorder.
 *
 * Note this does not make collisions impossible: two siblings whose labels
 * normalize to the same token (e.g. "A B" and "A-B") still share an id.
 * That is accepted deliberately — closing it needs a per-item
 * discriminator, and the only one available is position, whose instability
 * costs more than the collision it prevents. Sibling product menus that
 * differ only by punctuation are not a real case; missing labels are.
 */
export const toMenuToken = (
  item:
    | { localizationKey?: string; name?: string; url?: string }
    | null
    | undefined
): string => {
  // First *usable* of localizationKey -> name -> url, in preference order.
  // Checking usability rather than truthiness matters: `localizationKey ||
  // name` lets a junk-but-truthy key (say "***") shadow a perfectly good
  // name, and we'd fall all the way through to the url for no reason.
  for (const candidate of [item?.localizationKey, item?.name, item?.url]) {
    const token = toIdToken(candidate)
    if (token !== UNUSABLE) return token
  }
  return UNUSABLE
}
